import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import dcqService from '../../services/dcqService';
import { ChevronDown, ChevronUp, Search, Filter, Download, Loader, Plus, X, ChevronLeft, FileText } from 'lucide-react';
import CreateDCQ from './CreateDCQ/CreateDCQ';
import { AuthContext } from '../../contexts/AuthContext';
import hasUserRole from '../../utils/UserUtil';

const DCQs = () => {
  const { studyID, siteID } = useParams();
  const { user } = useContext(AuthContext);
  
  // Debug log when component loads
  console.log('DCQs component loaded, user:', user);
  const [dcqs, setDcqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    type: [],
    sitename: [],
  });
  const location = useLocation();
  const navigate = useNavigate();

  const showBackButton = false;
  const showExportButton = false;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      let response;
      const pathSegments = location.pathname.split('/').filter(segment => segment);

      if (pathSegments[0] === 'studies' && pathSegments.length === 2) {
        response = await dcqService.getDCQsForStudy(pathSegments[1]);
      } else if (pathSegments[0] === 'dcq' && pathSegments.length === 1) {
        response = await dcqService.getAllDCQs();
      } else if (pathSegments[0] === 'studies' && pathSegments[2] === 'sites' && pathSegments.length === 4) {
        response = await dcqService.getDCQsforSite(pathSegments[1], pathSegments[3]);
      } else {
        throw new Error('Invalid URL path');
      }

      if (response) {
        setDcqs(response);
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      setError('An error occurred while fetching the data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location]);

  const formatDate = (dateString) => {
    if (!dateString) return ' - ';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedDcqs = useMemo(() => {
    let sortableDcqs = [...dcqs];
    if (sortConfig.key) {
      sortableDcqs.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'ascending' 
            ? aValue - bValue
            : bValue - aValue;
        }

        if (sortConfig.key === 'updated_at' || sortConfig.key === 'created_at') {
          return sortConfig.direction === 'ascending'
            ? new Date(aValue) - new Date(bValue)
            : new Date(bValue) - new Date(aValue);
        }

        if (aValue == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'ascending' ? -1 : 1;
        return 0;
      });
    }
    return sortableDcqs;
  }, [dcqs, sortConfig]);

  const filteredDcqs = useMemo(() => {
    console.log('filteredDcqs useMemo triggered');
    console.log('sortedDcqs length:', sortedDcqs.length);
    console.log('Current user in filteredDcqs:', user);
    
    return sortedDcqs.filter(dcq => {
      // Basic search and filter checks
      const matchesSearch = Object.values(dcq).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesFilters = Object.entries(filters).every(([key, values]) => 
        values.length === 0 || values.includes(dcq[key])
      );
      
      // Site permission check - non-admin users should only see DCQs for their assigned sites
      let matchesSitePermissions = true;
      if (user && !hasUserRole(user, ['Program Admin', 'Project Admin']) && !user?.is_superuser) {
        console.log('Non-admin user detected, checking site permissions');
        console.log('User object keys:', Object.keys(user));
        console.log('DCQ being checked:', dcq);
        
        // TODO: Need to implement proper site assignment lookup
        // For now, show message that filtering is not working
        console.warn('Site filtering not implemented - user.assigned_sites field missing');
        // matchesSitePermissions = false; // Uncomment this to block all DCQs until backend provides site data
      }
      
      return matchesSearch && matchesFilters && matchesSitePermissions;
    });
  }, [sortedDcqs, searchTerm, filters, user]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDcqs.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderTableHeader = () => (
    <thead className="bg-gray-50 dark:bg-gray-900">
      <tr>
        {[
          { key: 'id', label: 'DCQ#' },
          { key: 'sitename', label: 'Site' },
          { key: 'participant', label: 'Participant' },
          { key: 'type', label: 'Type' },
          { key: 'action', label: 'Action' },
          { key: 'updated_at', label: 'Last Updated' },
          { key: 'created_at', label: 'Created on' },
          { key: 'status', label: 'Status' }
        ].map(({ key, label }) => (
          <th
            key={key}
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer whitespace-nowrap"
            onClick={() => requestSort(key)}
          >
            <div className="flex items-center">
              {label}
              {sortConfig.key === key && (
                sortConfig.direction === 'ascending' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderTableBody = () => (
    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
      {currentItems.map((dcq) => (
        <tr key={dcq.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
            <Link to={`/studies/${studyID}/dcq/${dcq.id}`}>{dcq.id}</Link>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {dcq.sitename || 'N/A'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {dcq.participant_display_id || dcq.participant || 'N/A'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dcq.type || 'N/A'}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{dcq.action || 'N/A'}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(dcq.updated_at)}</td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(dcq.created_at)}</td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              dcq.status === 'New' ? 'bg-green-100 text-green-800' :
              dcq.status === 'Needs Clarification' ? 'bg-yellow-100 text-yellow-800' :
              dcq.status === 'Waiting for Approval' ? 'bg-blue-100 text-blue-800' :
              dcq.status === 'Verification' ? 'bg-purple-100 text-purple-800' :
              dcq.status === 'Implementation' ? 'bg-orange-100 text-orange-800' :
              dcq.status === 'Closed' ? 'bg-gray-100 text-gray-800' :
              'bg-red-100 text-red-800'
            }`}>
              {dcq.status || 'N/A'}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  );

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const filterOptions = {
      status: ['New', 'Needs Clarification', 'Waiting for Approval', 'Verification', 'Implementation', 'Closed'],
      type: ['Query', 'Deviation', 'Other'],
      sitename: [...new Set(dcqs.map(dcq => dcq.sitename))].filter(Boolean),
    };

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow-md dark:shadow-gray-900/20">
        {Object.entries(filterOptions).map(([filterType, options]) => (
          <div key={filterType} className="mb-4">
            <h3 className="font-semibold mb-2 capitalize">{filterType}</h3>
            <div className="flex flex-wrap gap-2">
              {options.map(option => (
                <button
                  key={option}
                  onClick={() => toggleFilter(filterType, option)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filters[filterType].includes(option)
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        { showBackButton && (
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          <span>Back</span>
        </button>
        )}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary-50 text-primary-600 rounded-lg w-9 h-9 flex items-center justify-center">
            <FileText className="w-4.5 h-4.5" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Data Clarification Queries</h2>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        <div className="mb-6 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search DCQs..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 border rounded-md text-white ${
                showFilters ? 'bg-primary-600 border-primary-600' : 'bg-gray-400 border-gray-400'
              } hover:bg-opacity-90`}
            >
              <Filter size={20} className="mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            { showExportButton && (
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
              <Download size={20} className="mr-2" />
              Export
            </button>
            )}
          </div>
        </div>

        {showFilters && renderFilterOptions()}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="animate-spin text-primary-500" size={48} />
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg dark:shadow-gray-900/20">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {renderTableHeader()}
                  {renderTableBody()}
                </table>
              </div>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filteredDcqs.length === 0 ? 0 : indexOfFirstItem + 1}</span> to <span className="font-medium">{Math.min(indexOfLastItem, filteredDcqs.length)}</span> of{' '}
                  <span className="font-medium">{filteredDcqs.length}</span> results
                </p>
              </div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm dark:shadow-gray-900/20 -space-x-px" aria-label="Pagination">
                {Array.from({ length: Math.ceil(filteredDcqs.length / itemsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </nav>
              </div>
            </>
          )}
        </div>
        {showCreateForm && (
          <CreateDCQ onClose={() => setShowCreateForm(false)} onCreated={fetchData} />
        )}
      </div>
    );
  };
  
  export default DCQs;